import { type FunctionComponent, useEffect, useRef } from "react";

const WIDGETS = {
  general: {
    placementId: "99586393abcb7ec542f87e042adf0559",
    scriptUrl: "https://widget-view.dmm.com/js/placement.js",
  },
  r18: {
    placementId: "bedc7cb23b672773f9f32c2ec3c92db8",
    scriptUrl: "https://widget-view.dmm.co.jp/js/placement.js",
  },
};
type WidgetConfig = (typeof WIDGETS)[keyof typeof WIDGETS];
const WIDGET_ROOT_MARGIN = "100px";
const WIDGET_FRAME_TIMEOUT_MS = 15_000;
// 配信設定の728×90に合わせ、読み込み前から広告枠を確保する
const WIDGET_WIDTH = 728;
const WIDGET_HEIGHT = 90;

const createWidgetDocument = (config: WidgetConfig): string => `<!doctype html>
<html lang="ja">
<head><meta charset="utf-8"><title>DMM広告</title></head>
<body style="margin:0;background:transparent">
<ins class="dmm-widget-placement" data-id="${config.placementId}" style="background:transparent"></ins><script async src="${config.scriptUrl}" class="dmm-widget-scripts" data-id="${config.placementId}"></script>
</body>
</html>`;

interface Props {
  isR18?: boolean;
}

export const DmmWidget: FunctionComponent<Props> = ({
  isR18 = false,
}: Props) => {
  const config = isR18 ? WIDGETS.r18 : WIDGETS.general;
  const hostRef = useRef<HTMLElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    const root = rootRef.current;
    if (!host || !root) return;

    let disposed = false;
    let frame: HTMLIFrameElement | undefined;
    let loadTimeout: ReturnType<typeof setTimeout> | undefined;
    let observer: IntersectionObserver | undefined;

    const clearLoadTimeout = (): void => {
      if (loadTimeout === undefined) return;
      clearTimeout(loadTimeout);
      loadTimeout = undefined;
    };
    const onFrameLoad = (): void => {
      clearLoadTimeout();
    };
    const disposeFrame = (): void => {
      clearLoadTimeout();
      frame?.removeEventListener("load", onFrameLoad);
      frame?.remove();
      frame = undefined;
    };
    const createFrame = (): void => {
      if (
        disposed ||
        frame ||
        hostRef.current !== host ||
        rootRef.current !== root ||
        root.parentElement !== host
      ) {
        return;
      }

      const nextFrame = root.ownerDocument.createElement("iframe");
      nextFrame.title = "DMM広告";
      nextFrame.srcdoc = createWidgetDocument(config);
      nextFrame.setAttribute(
        "sandbox",
        "allow-scripts allow-popups allow-popups-to-escape-sandbox",
      );
      nextFrame.width = String(WIDGET_WIDTH);
      nextFrame.height = String(WIDGET_HEIGHT);
      nextFrame.className = "block border-0";
      nextFrame.addEventListener("load", onFrameLoad, { once: true });
      frame = nextFrame;
      loadTimeout = setTimeout(() => {
        if (frame === nextFrame) disposeFrame();
      }, WIDGET_FRAME_TIMEOUT_MS);
      root.append(nextFrame);
    };

    const IntersectionObserverConstructor =
      root.ownerDocument.defaultView?.IntersectionObserver;
    if (!IntersectionObserverConstructor) {
      createFrame();
    } else {
      observer = new IntersectionObserverConstructor(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;

          observer?.disconnect();
          observer = undefined;
          createFrame();
        },
        { rootMargin: WIDGET_ROOT_MARGIN },
      );
      observer.observe(root);
    }

    return (): void => {
      disposed = true;
      observer?.disconnect();
      disposeFrame();
    };
  }, [config]);

  return (
    <aside
      ref={hostRef}
      aria-label="DMM広告"
      className="overflow-x-auto overflow-y-hidden py-2"
    >
      <div
        key={config.placementId}
        ref={rootRef}
        id={`dmm-widget-${config.placementId}`}
        style={{
          width: WIDGET_WIDTH,
          height: WIDGET_HEIGHT,
          marginInline: "auto",
          background: "transparent",
        }}
      />
    </aside>
  );
};
